import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Prohibit,
  Plus,
  Trash,
  CircleNotch,
  MagnifyingGlass,
  ShieldWarning,
  ClockClockwise,
  User,
} from "@phosphor-icons/react";

/**
 * Admin page: block / unblock customer phones.
 *
 * • POST /api/admin/blocked-customers { phone, reason } → blocks
 * • GET  /api/admin/blocked-customers → list
 * • DELETE /api/admin/blocked-customers/{phone} → unblock
 *
 * Blocked phones still appear in the inbox (audit trail) but the bot
 * pipeline is skipped — no auto-replies, no welcome menu, no rodbez bot.
 */
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function BlockedCustomers() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newReason, setNewReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blocked-customers");
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load blocked list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitBlock = async () => {
    const phone = (newPhone || "").trim().replace(/^\+/, "");
    const reason = (newReason || "").trim();
    if (!phone || phone.length < 4) {
      toast.error("Phone number required (min 4 digits)");
      return;
    }
    if (!reason || reason.length < 2) {
      toast.error("Reason required (min 2 chars)");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/admin/blocked-customers", { phone, reason });
      toast.success(
        r.data?.was_already_blocked
          ? `Reason updated for ${phone}`
          : `Blocked ${phone}`
      );
      setShowAdd(false);
      setNewPhone("");
      setNewReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to block");
    } finally {
      setSubmitting(false);
    }
  };

  const unblock = async (phone) => {
    if (!window.confirm(`Unblock ${phone}?\n\nBot auto-replies will resume for this customer.`)) return;
    try {
      await api.delete(`/admin/blocked-customers/${encodeURIComponent(phone)}`);
      toast.success(`Unblocked ${phone}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unblock");
    }
  };

  const q = (query || "").trim().toLowerCase();
  const filtered = q
    ? items.filter((it) => {
        const blob = [it.phone, it.reason, it?.blocked_by?.email, it?.blocked_by?.full_name]
          .filter(Boolean).join(" ").toLowerCase();
        return blob.includes(q);
      })
    : items;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/")}
            className="p-2 rounded-md hover:bg-zinc-800 transition-colors"
            data-testid="back-btn"
            title="Back to inbox"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex items-center gap-2">
            <Prohibit size={20} weight="fill" className="text-rose-400" />
            <h1 className="text-lg font-semibold">Blocked Customers</h1>
            <span
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30"
              title="Only admins can manage this list"
            >
              Admin only
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors"
              data-testid="add-block-btn"
            >
              <Plus size={14} weight="bold" />
              Block customer
            </button>
            <button
              onClick={load}
              className="p-2 rounded-md hover:bg-zinc-800 transition-colors"
              data-testid="refresh-btn"
              title="Refresh"
            >
              <ClockClockwise size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-4">
        {/* Helper card */}
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3">
          <ShieldWarning size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-300 leading-relaxed">
            Blocking a customer ne stops the bot pipeline ONLY — no auto-replies,
            no welcome menu, no rodbez bot. The customer's incoming messages
            still appear in the inbox so agents can manually handle them.
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            type="text"
            placeholder="Search by phone / reason / admin name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-zinc-600"
            data-testid="search-input"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="py-16 text-center text-zinc-500">
            <CircleNotch size={20} className="inline-block animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="py-16 text-center text-zinc-500 text-sm"
            data-testid="empty-state"
          >
            {items.length === 0 ? "No blocked customers" : "No matches for your search"}
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
            <table className="w-full text-sm" data-testid="blocked-list">
              <thead className="text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-900/60">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Phone</th>
                  <th className="text-left px-4 py-2.5 font-medium">Reason</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Blocked by</th>
                  <th className="text-left px-4 py-2.5 font-medium hidden md:table-cell">Blocked at</th>
                  <th className="text-right px-4 py-2.5 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.phone}
                    className="border-t border-zinc-800 hover:bg-zinc-900/60 transition-colors"
                    data-testid={`row-${it.phone}`}
                  >
                    <td className="px-4 py-3 font-mono text-rose-300">+{it.phone}</td>
                    <td className="px-4 py-3 text-zinc-200">{it.reason}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-zinc-400">
                      <div className="flex items-center gap-1.5">
                        <User size={12} />
                        {it?.blocked_by?.full_name || it?.blocked_by?.email || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-zinc-500 text-xs">
                      {fmtTime(it.blocked_at)}
                      {it.last_updated_at && it.last_updated_at !== it.blocked_at && (
                        <div className="text-[10px] text-zinc-600">
                          updated {fmtTime(it.last_updated_at)}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => unblock(it.phone)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-300 border border-emerald-500/30 text-xs font-medium transition-colors"
                        data-testid={`unblock-${it.phone}`}
                      >
                        <Trash size={12} />
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="text-xs text-zinc-500">
          {filtered.length} of {items.length} blocked
        </div>
      </div>

      {/* Add block modal */}
      {showAdd && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !submitting && setShowAdd(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-5"
            onClick={(e) => e.stopPropagation()}
            data-testid="add-block-modal"
          >
            <div className="flex items-center gap-2 mb-4">
              <Prohibit size={18} weight="fill" className="text-rose-400" />
              <h2 className="text-base font-semibold">Block a customer</h2>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
                  Phone number
                </label>
                <input
                  type="text"
                  placeholder="919999000111 (with country code, no +)"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-zinc-600 font-mono"
                  data-testid="block-phone-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">
                  Reason <span className="text-rose-400">*</span>
                </label>
                <textarea
                  placeholder="Why are we blocking this customer? (will appear in the list)"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm focus:outline-none focus:border-zinc-600 resize-none"
                  data-testid="block-reason-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <button
                onClick={() => setShowAdd(false)}
                disabled={submitting}
                className="ml-auto px-3 py-1.5 rounded-md text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50"
                data-testid="block-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={submitBlock}
                disabled={submitting}
                className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1.5"
                data-testid="block-submit-btn"
              >
                {submitting && <CircleNotch size={12} className="animate-spin" />}
                Block
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
